import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { LocaleLink } from "@/components/site/LocaleLink";
import { LanguageSwitcher } from "@/components/site/LanguageSwitcher";
import { Menu, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/site/ThemeToggle";
import { Container } from "@/components/site/Container";
import { BrandWordmark } from "@/components/site/BrandWordmark";
import { business } from "@/content/site";
import { headerLinks as navLinks } from "./header-links";

const MobileMenu = lazy(() => import("./mobile-menu").then((module) => ({ default: module.MobileMenu })));

/** Routes that open with a full-bleed image hero the header may overlap. */
const overlayRoutes = [
  "/",
  "/nieuwbouw",
  "/renovatie",
  "/technieken",
  "/warmtepompen-afdekkingen",
  "/realisaties",
  "/opbouw-zwembad",
  "/over-ons",
  "/blog",
  "/offerte",
];

export function Header() {
  const [open, setOpen] = useState(false);
  const [menuRequested, setMenuRequested] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useTranslation();
  const rawPathname = useRouterState({ select: (state) => state.location.pathname });
  // Strip the language segment so the overlay logic keeps working per page.
  const pathname = rawPathname.replace(/^\/(nl|en|fr)(?=\/|$)/, "") || "/";
  const isOverlay = overlayRoutes.some((route) => pathname === route || (route === "/blog" && pathname.startsWith("/blog/")));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = !isOverlay || scrolled;

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 text-primary-foreground transition-[background-color,border-color] duration-200 ${
          solid
            ? "border-b border-primary-foreground/10 bg-primary/90 backdrop-blur supports-[backdrop-filter]:bg-primary/85"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <Container className="flex h-16 items-center justify-between gap-4 lg:h-20">
          <LocaleLink to="/" className="flex shrink-0 items-center">
            <BrandWordmark compact />
          </LocaleLink>

          <nav className="hidden items-center gap-0.5 2xl:flex">
            {navLinks.map((link) => (
              <LocaleLink
                key={link.to}
                to={link.to}
                activeOptions={{ exact: link.to === "/" }}
                activeProps={{ className: "text-secondary border-secondary" }}
                className="whitespace-nowrap border-b-2 border-transparent px-1.5 py-1.5 text-[13px] font-medium text-primary-foreground/85 transition-colors hover:text-secondary"
              >
                {t(`nav.${link.key}`)}
              </LocaleLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 2xl:flex">
            <LanguageSwitcher />
            <ThemeToggle label={t("actions.theme")} lightLabel={t("actions.themeLight")} darkLabel={t("actions.themeDark")} />
            <a
              href={`tel:${business.phoneE164}`}
              className="hidden items-center gap-1.5 whitespace-nowrap text-[13px] font-semibold text-primary-foreground hover:text-secondary min-[1700px]:inline-flex"
            >
              <Phone className="h-4 w-4" />
              {business.phone}
            </a>
            <Button asChild className="bg-secondary text-secondary-foreground hover:bg-secondary/90">
              <LocaleLink to="/offerte">{t("actions.quote")}</LocaleLink>
            </Button>
          </div>

          <div className="flex shrink-0 items-center gap-1 2xl:hidden">
            <ThemeToggle label={t("actions.theme")} lightLabel={t("actions.themeLight")} darkLabel={t("actions.themeDark")} />
            <Button
              variant="ghost"
              size="icon"
              asChild
              aria-label={t("actions.call")}
              className="text-primary-foreground hover:bg-primary-foreground/10 hover:text-secondary"
            >
              <a href={`tel:${business.phoneE164}`}>
                <Phone className="h-5 w-5" />
              </a>
            </Button>
                <Button
                  ref={menuTriggerRef}
                  variant="ghost"
                  size="icon"
                  aria-label={t("actions.openMenu")}
                  aria-haspopup="dialog"
                  aria-expanded={open}
                  onClick={() => {
                    setMenuRequested(true);
                    setOpen(true);
                  }}
                  className="text-primary-foreground hover:bg-primary-foreground/10 hover:text-secondary"
                >
                  <Menu className="h-6 w-6" />
                </Button>
            {menuRequested ? (
              <Suspense fallback={null}>
                <MobileMenu open={open} onOpenChange={setOpen} triggerRef={menuTriggerRef} />
              </Suspense>
            ) : null}
          </div>
        </Container>
      </header>
      {!isOverlay ? <div aria-hidden="true" className="h-16 lg:h-20" /> : null}
    </>
  );
}
